import React from 'react';
import styled from 'styled-components';
import { SkeletonBox, SkeletonImage, SkeletonLine } from './BaseSkeleton';

const Container = styled.div`
  max-width: ${props => props.theme.layout.containerMaxWidth};
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.sm};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  align-items: start;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const ImageGalleryWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const ImageContainer = styled.div`
  width: 100%;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const PriceSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SelectorGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  width: 100%;
`;

const SelectorLabel = styled.div`
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const SelectorButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  flex-wrap: wrap;
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ButtonSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const DescriptionSection = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const ProductDetailSkeleton: React.FC = () => {
  return (
    <Container>
      <ProductGrid>
        <ImageGalleryWrapper>
          <ImageContainer>
            <SkeletonImage $width="100%" $height="600px" />
          </ImageContainer>
        </ImageGalleryWrapper>
        
        <ProductInfoWrapper>
          <TitleSection>
            <SkeletonLine $width="80%" $height="32px" />
            <SkeletonLine $width="60%" $height="20px" />
          </TitleSection>
          
          <PriceSection>
            <SkeletonLine $width="100px" $height="24px" />
            <SkeletonLine $width="80px" $height="24px" />
          </PriceSection>
          
          <SkeletonLine $width="40%" $height="16px" />
          
          <SelectorGroup>
            <SelectorLabel>
              <SkeletonLine $width="60px" $height="16px" />
            </SelectorLabel>
            <SelectorButtons>
              <SkeletonBox $width="50px" $height="40px" />
              <SkeletonBox $width="50px" $height="40px" />
              <SkeletonBox $width="50px" $height="40px" />
              <SkeletonBox $width="50px" $height="40px" />
            </SelectorButtons>
          </SelectorGroup>
          
          <SelectorGroup>
            <SelectorLabel>
              <SkeletonLine $width="60px" $height="16px" />
            </SelectorLabel>
            <SelectorButtons>
              <SkeletonBox $width="40px" $height="40px" $borderRadius="50%" />
              <SkeletonBox $width="40px" $height="40px" $borderRadius="50%" />
              <SkeletonBox $width="40px" $height="40px" $borderRadius="50%" />
              <SkeletonBox $width="40px" $height="40px" $borderRadius="50%" />
            </SelectorButtons>
          </SelectorGroup>
          
          <QuantitySection>
            <SkeletonBox $width="40px" $height="40px" />
            <SkeletonBox $width="60px" $height="40px" />
            <SkeletonBox $width="40px" $height="40px" />
          </QuantitySection>
          
          <ButtonSection>
            <SkeletonBox $width="100%" $height="50px" />
          </ButtonSection>
          
          <DescriptionSection>
            <SkeletonLine $width="100%" $height="16px" />
            <SkeletonLine $width="100%" $height="16px" />
            <SkeletonLine $width="90%" $height="16px" />
            <SkeletonLine $width="95%" $height="16px" />
            <SkeletonLine $width="85%" $height="16px" />
          </DescriptionSection>
        </ProductInfoWrapper>
      </ProductGrid>
    </Container>
  );
};

export default ProductDetailSkeleton;
